export type PaymentMethodSetting = { methodCode: string; displayName: string; enabled: boolean; isDefault: boolean };
export type TaxAuthoritySetting = { taxAuthId: string; enabled: boolean; isDefault: boolean };
export type FinanceSettings = {
  partyId: string; version: number; updatedAtEpochMillis: number | null;
  paymentMethods: PaymentMethodSetting[]; taxAuthorities: TaxAuthoritySetting[];
};

const text = (value: unknown, max = 200): value is string => typeof value === "string" && value.trim().length > 0 && value.length <= max;
const bool = (value: unknown) => typeof value === "boolean";
const defaultsValid = (items: { enabled: boolean; isDefault: boolean }[]) =>
  items.filter((item) => item.isDefault).length <= 1 && items.every((item) => !item.isDefault || item.enabled);

/** A loaded settings document, never a URL or a platform role, owns the mutation scope and version. */
export function parseFinanceSettings(value: unknown, expectedPartyId: string): FinanceSettings {
  if (!value || typeof value !== "object") throw new Error("Invalid finance settings");
  const settings = value as FinanceSettings;
  if (!text(settings.partyId) || settings.partyId !== expectedPartyId || !Number.isSafeInteger(settings.version) || settings.version < 0 ||
    !(settings.updatedAtEpochMillis === null || Number.isSafeInteger(settings.updatedAtEpochMillis) && settings.updatedAtEpochMillis >= 0) ||
    !Array.isArray(settings.paymentMethods) || settings.paymentMethods.length > 16 ||
    settings.paymentMethods.some((item) => !item || !text(item.methodCode) || !text(item.displayName, 80) || item.displayName !== item.displayName.trim() || /[\u0000-\u001f\u007f-\u009f]/.test(item.displayName) || !bool(item.enabled) || !bool(item.isDefault)) ||
    !Array.isArray(settings.taxAuthorities) || settings.taxAuthorities.length > 32 ||
    settings.taxAuthorities.some((item) => !item || !text(item.taxAuthId) || !bool(item.enabled) || !bool(item.isDefault)) ||
    new Set(settings.paymentMethods.map((item) => item.methodCode)).size !== settings.paymentMethods.length ||
    new Set(settings.taxAuthorities.map((item) => item.taxAuthId)).size !== settings.taxAuthorities.length ||
    !defaultsValid(settings.paymentMethods) || !defaultsValid(settings.taxAuthorities)) {
    throw new Error("Invalid finance settings");
  }
  return settings;
}

export function updatePaymentMethod(settings: FinanceSettings, method: PaymentMethodSetting): FinanceSettings {
  const next = { ...method, isDefault: method.enabled && method.isDefault };
  const methods = settings.paymentMethods.some((item) => item.methodCode === next.methodCode)
    ? settings.paymentMethods.map((item) => item.methodCode === next.methodCode ? next : item)
    : [...settings.paymentMethods, next];
  return { ...settings, paymentMethods: methods.map((item) => next.isDefault && item.methodCode !== next.methodCode ? { ...item, isDefault: false } : item) };
}

export function updateTaxAuthority(settings: FinanceSettings, authority: TaxAuthoritySetting): FinanceSettings {
  const next = { ...authority, isDefault: authority.enabled && authority.isDefault };
  const authorities = settings.taxAuthorities.some((item) => item.taxAuthId === next.taxAuthId)
    ? settings.taxAuthorities.map((item) => item.taxAuthId === next.taxAuthId ? next : item)
    : [...settings.taxAuthorities, next];
  return { ...settings, taxAuthorities: authorities.map((item) => next.isDefault && item.taxAuthId !== next.taxAuthId ? { ...item, isDefault: false } : item) };
}

export function financeMutation(settings: FinanceSettings, requestId: string) {
  parseFinanceSettings(settings, settings.partyId);
  return {
    partyId: settings.partyId, requestId, expectedVersion: settings.version,
    paymentMethods: settings.paymentMethods.map(({ methodCode, displayName, enabled, isDefault }) => ({ methodCode, displayName: displayName.trim(), enabled, isDefault })),
    taxAuthorities: settings.taxAuthorities.map(({ taxAuthId, enabled, isDefault }) => ({ taxAuthId, enabled, isDefault })),
  };
}
